/**
 * useInvestmentItems Hook
 *
 * Custom hook for fetching and managing investment items data.
 * Uses the useLazyLoading hook for pagination.
 */

import { useMemo } from "react";
import { API_URL } from "constants/index";
import { useLazyLoading } from "shared/components/UniversalTable/hooks/useLazyLoading";
import type { InvestmentItem } from "../types/investment";

export interface UseInvestmentItemsFilters {
  buildingId?: string;
  status?: string;
  responsibleUserId?: string;
  searchText?: string;
  excludeConverted?: boolean;
  labelId?: number;
  labelType?: string;
  createdYear?: number;
  unitId?: string;
}

export interface UseInvestmentItemsReturn {
  items: InvestmentItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalCount: number | null;
  fetchItems: (append?: boolean) => Promise<void>;
  observerTarget: React.RefObject<HTMLDivElement>;
  hasActiveFilters: boolean;
}

/**
 * Hook for managing investment items data with lazy loading and filtering
 */
export function useInvestmentItems(
  filters: UseInvestmentItemsFilters = {}
): UseInvestmentItemsReturn {
  const {
    buildingId,
    status,
    responsibleUserId,
    searchText,
    excludeConverted,
    labelId,
    labelType,
    createdYear,
    unitId,
  } = filters;

  // Build additional params for API
  const additionalParams = useMemo(() => {
    const params: Record<string, string> = {};

    if (buildingId) params.buildingId = buildingId;
    if (status) params.status = status;
    if (responsibleUserId) params.responsibleUserId = responsibleUserId;
    if (searchText) params.searchText = searchText;
    if (excludeConverted) params.excludeConverted = 'true';
    if (labelId) params.labelId = String(labelId);
    if (labelType) params.labelType = labelType;
    if (createdYear) params.createdYear = String(createdYear);
    if (unitId) params.unitId = unitId;

    return params;
  }, [buildingId, status, responsibleUserId, searchText, excludeConverted, labelId, labelType, createdYear, unitId]);

  // Check if any filters are active
  const hasActiveFilters = Object.keys(additionalParams).length > 0;

  // Use lazy loading hook
  const {
    data,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    fetch: fetchItems,
    observerTarget,
  } = useLazyLoading<InvestmentItem>({
    endpoint: `${API_URL}/investment/items`,
    pageSize: 50,
    sortField: "createdAt",
    sortDirection: "DESC",
    additionalParams,
  });

  // Ensure items is always an array (defensive against API errors or initial load)
  const items = Array.isArray(data) ? data : [];

  return {
    items,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    fetchItems,
    observerTarget,
    hasActiveFilters,
  };
}
